import { ReactText, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import cx from 'classnames'
import { capitalize } from 'lodash'

import { Button } from 'components/Button'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { frontendOrigin } from 'utils/frontendOrigin'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import { Public } from 'almanac-ui/icons/24/Public'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { Eye } from 'almanac-ui/icons/16/Eye'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { ReactComponent as PublicBlue } from 'assets/icons/public32.svg'
import { ReactComponent as ToggleHorizontal } from 'assets/icons/toggleHorizontalDown16.svg'
import { ReactComponent as ToggleHorizontalUp } from 'assets/icons/toggleHorizontalUp16.svg'
import { ReactComponent as CopyIcon } from 'assets/icons/link16.svg'
import { ReactComponent as LockIcon } from 'assets/icons/locked16.svg'
import { ReactComponent as Trash } from 'assets/icons/trash16.svg'
import { copyTextToClipboard } from 'utils/articleUtils'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import useShareSheetV2 from 'hooks/useShareSheetV2'
import { Permission } from 'types'
import { ShareSheetProps, DropdownOptions } from './Types'
import { AccessDropdown } from './Access/AccessDropdown'
import { PasswordInput } from './Footer/PasswordInput'
import Styles from './ShareSheet.module.scss'

export const getPublicAccessOptions = (fileType: string, isFolderFlag: boolean): DropdownOptions[] => {
  if (isFolderFlag) {
    return [Permission.Edit, Permission.Read] as DropdownOptions[]
  }
  if (fileType === 'design') {
    return [Permission.Edit, Permission.Read] as DropdownOptions[]
  }
  return [Permission.Edit, Permission.Comment, Permission.Read] as DropdownOptions[]
}

export const Footer = ({ file }: ShareSheetProps) => {
  const {
    asset,
    userCanEdit,
    publicAccess,
    inheritedPublicAccess,
    createPublicAccess,
    removePublicAccess,
    selectedPermission,
    footerOpen,
    setShareSheet,
  } = useShareSheetV2(file)
  const location = useLocation()
  const currentWorkspace = useCurrentWorkspace()
  const [deletingAccess, setDeletingAccess] = useState(false)
  const [url, setUrl] = useState(location.pathname)

  const [toggleOn, setToggleOn] = useState(!!publicAccess || !!inheritedPublicAccess)
  const fullUrl = `${frontendOrigin()}${url}`
  const openWindowSafely = useOpenWindowSafely()

  useEffect(() => {
    if (!asset?.url) return
    setUrl(asset.url)
  }, [asset?.url])

  useEffect(() => {
    if (!currentWorkspace?.allow_public_links) return
    if (deletingAccess && publicAccess) return // Prevent toggle from turning while deleting

    if (deletingAccess && !publicAccess) {
      setDeletingAccess(false)
    }

    setToggleOn(!!publicAccess || !!inheritedPublicAccess)
  }, [publicAccess, inheritedPublicAccess, currentWorkspace?.allow_public_links]) // eslint-disable-line react-hooks/exhaustive-deps

  const copyLink = () => {
    copyTextToClipboard(fullUrl)
    showToastMessage('Link copied to the clipboard!', 'success')
  }

  const openInNewTab = () => {
    const docParams = `docView=${capitalize(publicAccess?.access) || 'Edit'}ing`
    if (file.type === 'folder') {
      const handbookKey = url.split('-').slice(-1)
      openWindowSafely(`${url}?focusView=${handbookKey}&${docParams}`)
      return
    }

    openWindowSafely(`${url}?${docParams}`)
  }

  const updatePublicAccess = (permission: Permission | null, password?: string | null) => {
    setToggleOn(!!permission)
    const onError = () => {
      if (!selectedPermission) setToggleOn(!permission)
    }
    if (!!permission) {
      createPublicAccess(permission, onError, password)
      return
    }

    if (!!publicAccess) {
      setDeletingAccess(true)
      removePublicAccess(publicAccess, onError)
    }
  }

  const isFolder = file.type === 'folder'

  const EditAccessContents = () => {
    const { isMobile } = useResponsiveDesign()

    return (
      <>
        {!footerOpen && toggleOn ? (
          <div className={Styles.openFooterButton} onClick={() => setShareSheet({ footerOpen: true })}>
            <PublicBlue />
            <span className={Styles.openFooterText}>Public Sharing</span>
            <div className={Styles.toggleIcon}>
              <ToggleHorizontalUp />
            </div>
          </div>
        ) : (
          <div className={Styles.shareToWeb}>
            <div className={Styles.shareToWebContents}>
              <div className={Styles.shareToWebLeft}>
                {toggleOn ? <PublicBlue /> : <Public size={32} color={Styles.grey40} />}
                <div className={Styles.shareToWebText}>
                  <div className={Styles.header}>Public - share to web</div>
                  <div className={Styles.subheader}>Publish and share link with anyone</div>
                </div>
              </div>

              <div className={Styles.shareToWebRight}>
                {toggleOn && (
                  <Button variant="tertiary" size="md" onPress={openInNewTab}>
                    Preview
                  </Button>
                )}
                <Tooltip hide={!inheritedPublicAccess} label="Shared publicly through parent folder.">
                  <div>
                    <ToggleButton
                      name="sharePublicly"
                      checked={toggleOn}
                      disabled={!!inheritedPublicAccess}
                      handleChange={() => updatePublicAccess(publicAccess ? null : Permission.Edit)}
                    />
                  </div>
                </Tooltip>
              </div>
            </div>
            {!isMobile && !!publicAccess && (
              <div className={Styles.publicAccessContainer}>
                <div className={Styles.accessLevel}>
                  <div className={Styles.accessLevelLeft}>
                    <Eye color={Styles.grey40} />
                    <span className={Styles.accessText}>Anyone with the link can…</span>
                  </div>
                  <AccessDropdown
                    options={getPublicAccessOptions(file.type, isFolder)}
                    currentPermission={publicAccess.access}
                    size="sm"
                    handleClick={(permission: ReactText) =>
                      updatePublicAccess(permission as Permission, publicAccess.password)
                    }
                  />
                </div>
                <div className={Styles.passwordContainer}>
                  <div className={Styles.passwordLeft}>
                    <div className={Styles.lockIcon}>
                      <LockIcon />
                    </div>
                    <div>Password</div>
                  </div>
                  <div className={Styles.passwordRight}>
                    <PasswordInput file={file} />
                    {!!publicAccess.password && (
                      <Button
                        className={Styles.deleteButton}
                        variant="tertiary"
                        size="lg"
                        onPress={() => updatePublicAccess(selectedPermission, null)}
                      >
                        <Trash />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
            {publicAccess && (
              <div className={Styles.closeFooterButton} onClick={() => setShareSheet({ footerOpen: false })}>
                <ToggleHorizontal />
              </div>
            )}
          </div>
        )}
      </>
    )
  }

  const NonEditorContents = () => {
    return (
      <div className={cx(Styles.shareToWeb, Styles.readOnly)}>
        {toggleOn ? <PublicBlue /> : <Public size={32} color={Styles.grey40} />}
        <div className={Styles.shareToWebText}>
          <div className={Styles.header}>{toggleOn ? 'Publicly shared' : 'Not shared publicly'}</div>
        </div>
      </div>
    )
  }

  return (
    <div className={Styles.footer}>
      {currentWorkspace?.allow_public_links &&
        !!asset &&
        asset?.type !== 'layer' &&
        (userCanEdit ? <EditAccessContents /> : <NonEditorContents />)}
      <div className={Styles.copyLinkContainer}>
        <div className={Styles.linkContainer}>{fullUrl}</div>
        <Button size="lg" onPress={copyLink} frontIcon={CopyIcon}>
          Copy Link
        </Button>
      </div>
    </div>
  )
}
